import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import type { Bloco, BlocoKanbanColumn } from '@/types/bloco'
import { useBlocosDnd } from '@/hooks/useBlocosDnd'
import { useBlocosByColumn } from './useBlocosByColumn'
import { TarefasKanbanColumn } from './TarefasKanbanColumn'

interface TarefasKanbanBoardProps {
  blocos: Bloco[]
  columns: BlocoKanbanColumn[]
  loading?: boolean
  currentUserId?: string
  labelFilterId?: string | null
  onMoveColumn: (blocoId: string, columnId: string) => void
  onOpenBloco: (id: string) => void
}

export function TarefasKanbanBoard({
  blocos,
  columns,
  loading = false,
  currentUserId,
  labelFilterId,
  onMoveColumn,
  onOpenBloco,
}: TarefasKanbanBoardProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [showLeft, setShowLeft] = useState(false)
  const [showRight, setShowRight] = useState(false)

  const columnMap = useBlocosByColumn(blocos, columns, currentUserId, labelFilterId)
  const dnd = useBlocosDnd(onMoveColumn)

  const checkScroll = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    setShowLeft(el.scrollLeft > 8)
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8)
  }, [])

  const scroll = useCallback((direction: 'left' | 'right') => {
    containerRef.current?.scrollBy({ left: direction === 'left' ? -320 : 320, behavior: 'smooth' })
  }, [])

  if (loading) {
    return (
      <div className='flex items-center justify-center py-16'>
        <div className='h-6 w-6 animate-spin rounded-full border-2 border-[var(--brand-primary)]/30 border-t-[var(--brand-primary)]' />
      </div>
    )
  }

  if (columns.length === 0) {
    return (
      <p className='py-10 text-center text-sm text-text-muted'>
        Configure suas colunas do Kanban para começar.
      </p>
    )
  }

  return (
    <div className='relative'>
      {showLeft && (
        <button type='button' onClick={() => scroll('left')} className='kanban-nav-btn left-btn'>
          <ChevronLeft size={16} />
        </button>
      )}
      {showRight && (
        <button type='button' onClick={() => scroll('right')} className='kanban-nav-btn right-btn'>
          <ChevronRight size={16} />
        </button>
      )}

      <div
        ref={containerRef}
        onScroll={checkScroll}
        className='scrollbar-thin flex gap-3 overflow-x-auto pb-4'
      >
        {columns.map((column) => (
          <TarefasKanbanColumn
            key={column.id}
            column={column}
            items={columnMap.get(column.id) ?? []}
            currentUserId={currentUserId}
            draggedBlocoId={dnd.draggedBlocoId}
            isDragOver={dnd.draggedOverColumn === column.id}
            onDragOver={dnd.handleDragOver}
            onDragEnter={dnd.handleDragEnter}
            onDragLeave={dnd.handleDragLeave}
            onDrop={dnd.handleDrop}
            onDragStart={dnd.handleDragStart}
            onDragEnd={dnd.handleDragEnd}
            onOpen={onOpenBloco}
          />
        ))}
      </div>
    </div>
  )
}
